import React, { ReactNode, useContext, useEffect, useReducer } from 'react';
import { useIntercom } from 'react-use-intercom';

import { localstorageNamesToDelete } from 'common/helpers';
import { ACCESS_TOKEN, REFRESH_TOKEN } from 'consts/auth';
import { ISignUpModel } from 'modules/account/features/singup/models';
import API from 'services/api';
import * as Logger from 'services/logger';
import { syncPushSubscription, unregisterPushSubscription } from 'services/pushNotifications';
import { signalRService } from 'services/signalr/SignalRService';

const REGISTER_USER = 'REGISTER_USER';
const SET_TOKENS = 'SET_TOKENS';
const LOGOUT = 'LOGOUT';

const initialState = {
  isRegistrationPending: false,
  email: '',
  accessToken: window.localStorage.getItem(ACCESS_TOKEN) || null,
  refreshToken: window.localStorage.getItem(REFRESH_TOKEN) || null,
  isAuthenticated: !!window.localStorage.getItem(ACCESS_TOKEN),
  formValues: {},
};

export const AuthState = React.createContext({
  state: { ...initialState },
  registerUser: (_email: string, _formValues?: ISignUpModel) => {},
  setTokens: (_accessToken: string, _refreshToken: string) => {},
  logout: () => {},
});

export const useAuthState = () => useContext(AuthState);

type AuthAction =
  | {
      type: 'REGISTER_USER';
      isRegistrationPending: boolean;
      email: string;
      formValues: ISignUpModel;
    }
  | { type: 'SET_TOKENS'; accessToken: string; refreshToken: string; isAuthenticated: boolean }
  | { type: 'LOGOUT'; isAuthenticated: boolean };

const reducer = (state = initialState, action: AuthAction) => {
  switch (action.type) {
    case REGISTER_USER:
      return {
        ...state,
        isRegistrationPending: action.isRegistrationPending,
        email: action.email,
        formValues: action.formValues,
      };
    case SET_TOKENS:
      return {
        ...state,
        accessToken: action.accessToken,
        refreshToken: action.refreshToken,
        isAuthenticated: action.isAuthenticated,
      };
    case LOGOUT:
      return {
        ...state,
        isAuthenticated: action.isAuthenticated,
      };
    default:
      return state;
  }
};

interface IProps {
  children: ReactNode;
}

const AuthProvider = ({ children }: IProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { shutdown } = useIntercom();

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === REFRESH_TOKEN) {
        if (event.newValue !== state.refreshToken) {
          window.location.reload();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [state.refreshToken]);

  useEffect(() => {
    if (state.isAuthenticated) {
      syncPushSubscription().catch(() => undefined);
    }
  }, [state.isAuthenticated]);

  const registerUser = (email: string, formValues: any) => {
    dispatch({ type: REGISTER_USER, isRegistrationPending: true, email, formValues });
  };

  const setTokens = (accessToken: string, refreshToken: string) => {
    window.localStorage.setItem(ACCESS_TOKEN, accessToken);
    window.localStorage.setItem(REFRESH_TOKEN, refreshToken);
    dispatch({ type: SET_TOKENS, accessToken, refreshToken, isAuthenticated: true });
  };

  const logout = async () => {
    const refreshToken = window.localStorage.getItem(REFRESH_TOKEN);

    await unregisterPushSubscription().catch(() => undefined);

    // Prvo pocisti klijenta: cim server povuce token svaki poziv u letu vraca 401, a SignalR se
    // pokusava spojiti natrag. Tek onda javi serveru.
    signalRService.stop();
    shutdown();
    Logger.clearUser();
    localstorageNamesToDelete([
      ACCESS_TOKEN,
      REFRESH_TOKEN,
      'filter',
      'IS_GLOBAL_EDIT_MODE',
      'IS_EDIT_MODE',
      'OBJECT_TYPE',
      'PROPERTY_ID',
      'PROPERTY_CODE',
      'PROPERTY_NAME',
      'UNIT_ID',
      'UNIT_CODE',
      'UNIT_NAME',
      'UNIT_TYPE',
    ]);
    dispatch({ type: LOGOUT, isAuthenticated: false });

    // Odjava i na serveru: brise refresh token (best-effort). Stariji backend jos nema ovu rutu (404) -
    // svejedno nastavi s odjavom u pregledniku.
    if (refreshToken) {
      try {
        const formData = new FormData();
        formData.append(REFRESH_TOKEN, refreshToken);
        await API.post('/connect/logout', formData, { timeout: 5000 });
      } catch {
        // ignoriraj - odjava u pregledniku ide dalje bez obzira na server
      }
    }
  };

  const value = {
    state,
    registerUser,
    setTokens,
    logout,
  };

  return <AuthState.Provider value={value}>{children}</AuthState.Provider>;
};

export default AuthProvider;
